/**
 * V2 audit point 1: a permanent LinkedIn indicator for the Home topbar.
 * - career_profile.linkedin_url present → a quiet "linked ✓" badge.
 * - absent → a "link your LinkedIn" button opening a small flow:
 *     1) the public profile URL field (what the radar actually needs;
 *        saved to career_profile.linkedin_url via careerProfile.update), and
 *     2) an OPTIONAL identity link via supabase.auth.linkIdentity
 *        (linkedin_oidc). The OIDC flow never returns the public profile URL,
 *        so the URL field stays the primary action. If manual linking is
 *        disabled in the Supabase dashboard the identity button degrades to an
 *        honest message and the URL flow still works fully.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BadgeCheck, Link2 } from 'lucide-react';
import Sheet from '@/components/v2/Sheet';
import Button from '@/components/v2/Button';
import { supabase } from '@/lib/supabase';
import { trpc } from '@/lib/trpc';

/** Accepts linkedin.com/in/... in any casual form; returns the normalized URL. */
function normalizeLinkedinUrl(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  const withProto = /^https?:\/\//i.test(v) ? v : `https://${v}`;
  try {
    const u = new URL(withProto);
    if (!/(^|\.)linkedin\.com$/i.test(u.hostname)) return null;
    if (!/^\/in\/[^/]+/i.test(u.pathname)) return null;
    return `https://www.linkedin.com${u.pathname.replace(/\/+$/, '')}`;
  } catch {
    return null;
  }
}

export default function LinkedInLinkControl(props: {
  /**
   * Controlled mode: Home passes the value + onSaved from its own state.
   * Omit the prop entirely for self-contained mode (DesktopTopbar): the
   * control fetches and manages its own linkedin_url so it can be dropped
   * anywhere without prop threading.
   */
  linkedinUrl?: string | null;
  onSaved?: (url: string) => void;
}) {
  const { linkedinUrl: linkedinUrlProp, onSaved } = props;
  const controlled = linkedinUrlProp !== undefined;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selfUrl, setSelfUrl] = useState<string | null>(null);
  const [selfLoaded, setSelfLoaded] = useState(false);

  // Self-contained mode: fetch the saved URL once on mount. Render nothing
  // until it resolves so users who ARE linked never see a "connect" flash.
  useEffect(() => {
    if (controlled) return;
    let cancelled = false;
    trpc.careerProfile.get({ section: 'radar' })
      .then((r) => { if (!cancelled) { setSelfUrl(r?.profile?.linkedin_url ?? null); setSelfLoaded(true); } })
      .catch(() => { if (!cancelled) setSelfLoaded(true); });
    return () => { cancelled = true; };
  }, [controlled]);

  const linkedinUrl = controlled ? linkedinUrlProp : selfUrl;
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [identityMsg, setIdentityMsg] = useState<string | null>(null);
  const [identityWorking, setIdentityWorking] = useState(false);

  // Self-contained mode: stay invisible until the fetch resolves (no flash).
  if (!controlled && !selfLoaded) return null;

  if (linkedinUrl) {
    return (
      <span className="inline-flex items-center gap-1 rounded-v2-pill bg-emerald-50 px-2 py-1 font-ar text-[11px] font-semibold text-emerald-700">
        <BadgeCheck size={13} aria-hidden="true" />
        {t('dashboard.linkedin.linked')}
      </span>
    );
  }

  async function handleSave() {
    const normalized = normalizeLinkedinUrl(url);
    if (!normalized) {
      setError(t('dashboard.linkedin.invalidUrl'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await trpc.careerProfile.update({ linkedin_url: normalized });
      onSaved?.(normalized);
      if (!controlled) setSelfUrl(normalized);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleLinkIdentity() {
    setIdentityWorking(true);
    setIdentityMsg(null);
    try {
      const { error: linkError } = await supabase.auth.linkIdentity({
        provider: 'linkedin_oidc',
        options: {
          redirectTo: `${window.location.origin}/home`,
          scopes: 'openid profile email',
        },
      });
      // On success the browser is redirected away; reaching here with an
      // error usually means manual linking is disabled server-side.
      if (linkError) setIdentityMsg(t('dashboard.linkedin.identityUnavailable'));
    } catch {
      setIdentityMsg(t('dashboard.linkedin.identityUnavailable'));
    } finally {
      setIdentityWorking(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-v2-pill border border-teal-200 bg-teal-50 px-2 py-1 font-ar text-[11px] font-semibold text-teal-700 hover:bg-teal-100"
      >
        <Link2 size={13} aria-hidden="true" />
        {t('dashboard.linkedin.connect')}
      </button>

      <Sheet open={open} onClose={() => setOpen(false)} title={t('dashboard.linkedin.sheetTitle')} snapPoints={[50, 90]}>
        <p className="font-ar text-[13px] leading-relaxed text-v2-body">
          {t('dashboard.linkedin.explain')}
        </p>

        <label className="mt-4 block font-ar text-[12px] font-bold text-v2-ink" htmlFor="li-url">
          {t('dashboard.linkedin.urlLabel')}
        </label>
        <input
          id="li-url"
          dir="ltr"
          type="url"
          inputMode="url"
          value={url}
          onChange={(e) => { setUrl(e.target.value); setError(null); }}
          placeholder="https://www.linkedin.com/in/username"
          className="mt-1.5 w-full rounded-v2-md border border-v2-line bg-white px-3 py-2.5 text-left font-en text-[13px] text-v2-ink outline-none placeholder:text-v2-mute focus:border-teal-400"
        />
        {error && <p className="mt-1.5 font-ar text-[12px] text-rose-700">{error}</p>}
        <Button
          variant="primary"
          fullWidth
          className="mt-3"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? t('dashboard.linkedin.saving') : t('dashboard.linkedin.save')}
        </Button>

        <div className="mt-5 border-t border-v2-line pt-4">
          <p className="font-ar text-[12px] leading-relaxed text-v2-mute">
            {t('dashboard.linkedin.identityHint')}
          </p>
          <Button
            variant="secondary"
            fullWidth
            className="mt-2"
            onClick={handleLinkIdentity}
            disabled={identityWorking}
          >
            {identityWorking ? t('dashboard.linkedin.saving') : t('dashboard.linkedin.identityCta')}
          </Button>
          {identityMsg && <p className="mt-2 font-ar text-[12px] text-amber-700">{identityMsg}</p>}
        </div>
      </Sheet>
    </>
  );
}
